import { Flex, Image, Typography } from "antd";
import StampExampleIcon from "@/shared/assets/png/stamp-example.png";
import {
  AttachIcon,
  StatusDeclinedIcon,
  StatusPendingIcon,
  StatusSignedIcon,
} from "@/shared/assets/svg/icons";
import { DocstoreSections } from "../../../../../model";
import { ColumnConfig, TableConfig } from "./types";
import "../docstore.section.table.scss";

const getStampStatusIcon = (status: string) => {
  if (status === "signed") {
    return <StatusSignedIcon />;
  }

  if (status === "declined") {
    return <StatusDeclinedIcon />;
  }

  return <StatusPendingIcon />;
};

const fullNameColumn: ColumnConfig = {
  title: (
    <Flex vertical>
      <Typography.Text className="title-style">Наименование</Typography.Text>
      <Typography.Text className="title-style">Описание</Typography.Text>
    </Flex>
  ),
  dataIndex: "fullName",
  key: "fullName",
  render: (value) => (
    <Flex vertical>
      <Typography.Text style={{ color: "#1677ff" }}>{value?.name}</Typography.Text>
      <Typography.Text type="secondary">{value?.description}</Typography.Text>
    </Flex>
  ),
  sorter: {
    compare: (a, b) => {
      if (!a.fullName || !b.fullName) return 0;
      return a.fullName?.name.localeCompare(b.fullName?.name);
    },
    multiple: 3,
  },
};

const docNumberColumn: ColumnConfig = {
  title: "Номер документа",
  dataIndex: "docNumber",
  key: "docNumber",
  sorter: {
    compare: (a, b) => {
      if (!a.docNumber || !b.docNumber) return 0;
      return a.docNumber?.localeCompare(b.docNumber);
    },
    multiple: 3,
  },
};

const dateColumn: ColumnConfig = {
  title: "Дата выдачи",
  dataIndex: "date",
  key: "date",
  sorter: {
    compare: (a, b) => {
      if (!a.date || !b.date) return 0;
      return a.date?.localeCompare(b.date);
    },
    multiple: 3,
  },
};

const issuerColumn: ColumnConfig = {
  title: "Орг. выдавшая документ",
  dataIndex: "issuer",
  key: "issuer",
};

const stampsColumn: ColumnConfig = {
  title: "Штампы",
  dataIndex: "stamps",
  key: "stamps",
  render: (value: { id: string; name: string; status: string }[]) => (
    <Flex gap="10px" align="start" vertical>
      {value.map((stamp: { id: string; name: string; status: string }) => (
        <Flex gap="4px" align="center" key={stamp.id}>
          <Image
            src={StampExampleIcon}
            alt={stamp.name}
            width="114px"
            height="24px"
            preview={false}
          />
          {getStampStatusIcon(stamp.status)}
        </Flex>
      ))}
    </Flex>
  ),
};

const authorColumn: ColumnConfig = {
  title: "Автор",
  dataIndex: "author",
  key: "author",
  render: (value) => (
    <Flex gap="2px" vertical>
      <Typography.Text style={{ color: "#1677ff" }}>{value?.name}</Typography.Text>
      <Typography.Text type="secondary">{value?.org}</Typography.Text>
    </Flex>
  ),
};

const docNameColumn: ColumnConfig = {
  title: "Документ",
  dataIndex: "docName",
  key: "docName",
  render: (value) => (
    <Flex gap="2px" vertical>
      <Typography.Text style={{ color: "#1677ff" }}>{value?.name}</Typography.Text>
      <Typography.Text type="secondary">{value?.description}</Typography.Text>
    </Flex>
  ),
  sorter: {
    compare: (a, b) => {
      if (!a.docName || !b.docName) return 0;
      return a.docName?.name.localeCompare(b.docName?.name);
    },
    multiple: 3,
  },
};

const workDrawColumn: ColumnConfig = {
  title: "Рабочий чертёж",
  dataIndex: "workDraw",
  key: "workDraw",
  render: (value) => (
    <Flex gap="2px" align="center">
      <AttachIcon style={{ width: "16px", height: "16px" }} />
      <Typography.Text>{value}</Typography.Text>
    </Flex>
  ),
};

const codeColumn: ColumnConfig = {
  title: "Шифр",
  dataIndex: "code",
  key: "code",
};

const pageColumn: ColumnConfig = {
  title: "Лист",
  dataIndex: "page",
  key: "page",
};

const expiryDateColumn: ColumnConfig = {
  title: "Дата окончания срока действия",
  dataIndex: "expiryDate",
  key: "expiryDate",
};

const producerColumn: ColumnConfig = {
  title: "Производитель",
  dataIndex: "producer",
  key: "producer",
};

const createActionColumn = (): ColumnConfig => ({
  title: "",
  dataIndex: "action",
  key: "action",
  width: 52,
});

// Колонки для разрешительной документации
const permitsColumns: ColumnConfig[] = [
  fullNameColumn,
  docNumberColumn,
  dateColumn,
  issuerColumn,
  stampsColumn,
  authorColumn,
];

// Колонки для проектной документации
const projectColumns: ColumnConfig[] = [
  docNameColumn,
  dateColumn,
  issuerColumn,
  codeColumn,
  pageColumn,
  stampsColumn,
  authorColumn,
];

// Колонки для рабочей документации
const workColumns: ColumnConfig[] = [
  docNameColumn,
  dateColumn,
  issuerColumn,
  codeColumn,
  pageColumn,
  stampsColumn,
  authorColumn,
];

// Колонки для исполнительных схем
const executiveSchemesColumns: ColumnConfig[] = [
  docNameColumn,
  dateColumn,
  issuerColumn,
  codeColumn,
  pageColumn,
  workDrawColumn,
  stampsColumn,
  authorColumn,
];

// Колонки для исполнительных чертежей
const executiveDrawingsColumns: ColumnConfig[] = [
  docNameColumn,
  dateColumn,
  issuerColumn,
  codeColumn,
  pageColumn,
  workDrawColumn,
  stampsColumn,
  authorColumn,
];

// Колонки для документов подтверждающих качество
const qualityConfirmationColumns: ColumnConfig[] = [
  docNameColumn,
  docNumberColumn,
  dateColumn,
  issuerColumn,
  expiryDateColumn,
  producerColumn,
  stampsColumn,
  authorColumn,
];

// Колонки для лабораторий
const laboratoriesColumns: ColumnConfig[] = [
  fullNameColumn,
  docNumberColumn,
  dateColumn,
  issuerColumn,
  stampsColumn,
  authorColumn,
];

// Колонки для сопроводительных документов
const accompanyingDocumentsColumns: ColumnConfig[] = [
  fullNameColumn,
  docNumberColumn,
  dateColumn,
  issuerColumn,
  stampsColumn,
  authorColumn,
];

// Колонки для нормативной документации
const normativeDocumentsColumns: ColumnConfig[] = [
  fullNameColumn,
  docNumberColumn,
];

// Конфигурация таблиц для каждого раздела
export const createDocstoreTableConfig = (): Record<
  Exclude<keyof typeof DocstoreSections, "TRASH">,
  TableConfig
> => ({
  PERMITS: {
    columns: [...permitsColumns, createActionColumn()],
  },
  WORKING_DRAFT: {
    columns: [...projectColumns, createActionColumn()],
  },
  WORK: {
    columns: [...workColumns, createActionColumn()],
  },
  EXECUTIVE_SCHEME: {
    columns: [...executiveSchemesColumns, createActionColumn()],
  },
  EXECUTIVE_DRAWING: {
    columns: [...executiveDrawingsColumns, createActionColumn()],
  },
  CERTIFICATE: {
    columns: [...qualityConfirmationColumns, createActionColumn()],
  },
  LABORATORY_DATA: {
    columns: [...laboratoriesColumns, createActionColumn()],
  },
  SUPPORTING: {
    columns: [...accompanyingDocumentsColumns, createActionColumn()],
  },
  NORMATIVE: {
    columns: [...normativeDocumentsColumns, createActionColumn()],
  },
});
